import * as _ from 'lodash';
import * as moment from 'moment';
import {createFeatureSelector, createSelector} from '@ngrx/store';
import {format} from 'date-fns';

import {TimecardEntryState, TimecardFeatureState} from '../reducers';
import {
  UsaTimecard,
  ApprovalTask,
  Batch,
  BreakageTotals,
  DayType,
  GetApprovalTaskByRole,
  MobileApprovalCircle, MobileApprovalLoop,
  MobileShifts,
  PayFactor,
  PayFactorTotal,
  SagDetail,
  ShiftDate,
  ShiftDates,
  ShiftRate,
  Timecard,
  ValidationError,
  ValueError
} from '../../../../common/timecard.model';
import {Startcard} from '../../../../common/start-card.model';
import {
    selectPermissions,
    selectProjectPermissions,
    selectProjectRoleId,
    selectProjectSettings,
    selectProjectUserWorkflowIds,
    selectSelectedProject,
    selectSelectedProjectId,
    selectUserRole
} from '../../auth/selectors';
import {Project} from '../../../../common/project.model';
import {GridColumn} from '../../shared/components/grid-column-filter/grid-column-filter-view.model';
import {Discrepancy, DiscrepancyViewModel, PunchTab, PunchType, Shift, SummaryLine} from '../models/timecard-entry.view.model';
import * as fromTimecardEntryUISelectors from '../selectors/timecard-entry-ui.selector';
import {selectAccountCodeConfig, selectChangedSummaryLineId, selectSelectedPunchTab, selectTimecardEntryDirtyStatus} from './timecard-entry-ui.selector';
import * as fromCommonConstants from '../../../../common/constants';
import {Area, DayOfWeek, Field} from '../../../../common/constants';
import {selectActiveTimecardType, selectIsWorkLocationPickerOpened} from '../../selectors/ui.selectors';
import {AccountCodeConfig, AccountCodeSegmentConfig, ProjectSetting} from '../../../../common/project-setting.model';
import {selectDailyTimeEntries, selectMobileApprovalStatus, selectMobileTimeEntries} from './timecard-entry-lookup-data.selector';
import {
    Permission,
    PermissionMap,
    PermissionType,
    ProjectPermissions,
    UserHasPermission,
    UserHasPermissions,
    UserIsInRole
} from '../../../../common/user-profile.model';
import {MobileShiftNoteDetail} from '../../../../common/timecard-note.model';
import {LiteCrew} from '../../startcard/models/startcard-details-view.model';
import {TimecardEntryStore} from '../reducers/timecard-entry.reducer';
import {ApiInvoiceStatus} from '../../../../common/paymaster-batch.model';
import {IsValidUUID} from '../../../../common/utilities/uuid';
import {CalculateOrderedShifts, CalculateOrderedWeekDays, HasPayPunchFields, IsShiftTimesEmpty, IsSplitShift} from '../../../../common/utilities/timecard';
import {selectIsSavedTimecard} from '../../core/selectors';
import {MapToBreakageOverrideViewModel} from '../models/breakage-override.view.model';
import {GrossPay, WeeklyOverTimeSummary} from '../../../../common/htg-timecard.submodel';
import {GetAllPossibleShifts, GetShiftDiscrepancies, PossibleShift} from '../../../../common/services/timecard-discrepancy.service';
import {TimecardDetailConstants} from '../../../../constants/timecard-details.constants';
import {BatchStatus} from '../../../../common/batch.model';
import {selectCrewDayType1LookupData} from '../../selectors/lookup.selector';
import {JobDetails, Schedule} from '../../../../common/contract-rate.model';
import {SagTimecardDetailConstants} from '../../../../constants/sag/sag-timecard-details.constants';
import {
  ApprovalStatus,
  ErrorType,
  InternalUserRole,
  MealPenaltyEnum,
  MobileApprovalStatus,
  TimecardStatus,
  TimecardTypes,
  WorkflowApprovalStatus
} from '../../../../common/timecard.enum';
import {
    AccountCode,
    AccountCoding,
    Breakage,
    Breakages,
    CrewDetail,
    GetCrewDetail,
    GetSagDetail,
    WorkStatus,
     UsaPunch,
     UsaShifts,
} from '../../../../common/timecard.shifts.model';
import {
  FlatSummaryLine,
  PreviousSummaryValues,
  SummaryAccountCode,
  SummaryBasics,
  SummaryDate,
  SummaryItems,
  SummaryJobDetail,
  SummaryLineChanges,
  SummaryPayDetail,
} from '../../../../common/timecard.summary-items.model';
import {ErrorMessages} from '../../../../common/error-messages.en';
import {StandardDateFormatString as StandardDateFormatString1, StandardDateFormatString} from '../../../../common/date-format.constants';
import {selectRegionWeekStartsOn} from '../../selectors/region.selector';

const _selectTimecardFeatureState = createFeatureSelector<TimecardFeatureState>('timecard');
const _selectTimecardEntryState = (state: TimecardFeatureState) => state ? state.timecardEntry : null;
const _selectTimecard = (state: TimecardEntryState): TimecardEntryStore => state ? state.timecard : null;
const _selectTimecardId = (state: TimecardEntryState) => {
  if (!state) {
    return '';
  }
  return state.timecard ? state.timecard.timecardId : '';
};
const _selectStartcard = (state: UsaTimecard) => state ? state.startcard : null;
const _selectWeekEndingDate = (state: UsaTimecard): string => state ? state.weekEndingDate : '';
const _selectStartcardId = (state: Startcard) => state ? state.startcardId : '';
const _selectTimecardErrors = (state: UsaTimecard) => state && state.errors ? state.errors : [];

const _selectShifts = (state: UsaTimecard, dtShifts: UsaShifts, mtShifts: MobileShifts, punchTab: PunchTab) => {
  if (punchTab === PunchTab.Pay_Times) {
    return state && state.shifts;
  }
  if (punchTab === PunchTab.Daily_Times) {
    return dtShifts;
  }
  return mtShifts;
};
const _selectShiftDays = (state: UsaShifts|MobileShifts): ShiftDate[] => _.map(state ? state.shiftDates : null, (shiftDay) => shiftDay);
const _selectShiftIds = (state: ShiftDate[]): string[] => state ? _.map(state, 'shiftId') : [];
const _selectPayPunches = (state: UsaShifts|MobileShifts): UsaPunch[] => _.map(state ? state.payPunches : null, v => v);
const _selectShiftRates = (state: UsaShifts): ShiftRate[] => _.map(state ? state.shiftRates : null, (shiftRate) => shiftRate);
const _selectDetails = (state: UsaShifts, activeTimecardType: TimecardTypes): CrewDetail[] =>
  activeTimecardType === TimecardTypes.CREW && _.map(state ? state.workDetails : null, (workDetail) => GetCrewDetail(workDetail));
const _selectBreakages = (state: UsaShifts): Breakage[] => _.map(state ? state.breakages : null, (breakage) => breakage);
const _selectTcShifts = (state: UsaTimecard) => state ? state.shifts : null;
const _selectBreakageTotal = (state: UsaTimecard, punchTab: PunchTab): BreakageTotals => state && punchTab === PunchTab.Pay_Times
  ? state.breakageTotals
  : null;

const _selectShift = (state: UsaShifts, shiftId: string, activeTimecardType: TimecardTypes): Shift =>
  state && activeTimecardType === TimecardTypes.CREW && shiftId ?
    {
      shiftId: shiftId,
      shiftDate: state.shiftDates[shiftId],
      shiftRate: state.shiftRates[shiftId],
      workDetail: GetCrewDetail(state.workDetails[shiftId]),
      accountCode: state.accountCodes[shiftId],
      payPunch: state.payPunches[shiftId],
      prPunch: state.prPunches[shiftId],
      mobilePunch: state.mobilePunches[shiftId]
    } : null;
const _selectSummaryItems = (state: UsaTimecard): SummaryItems => state ? state.summaryItems : null;
const _selectSummaryPreviousValues = (state: SummaryItems): PreviousSummaryValues => state && state.previousValues ?
  state.previousValues :
  null;
const _selectSummaryLine = (state: SummaryItems, summaryId: string): SummaryLine => state && summaryId ?
  {
    summaryId: summaryId,
    date: state.dates[summaryId],
    jobDetail: state.jobDetails[summaryId],
    accountCode: state.accountCodes[summaryId],
    basics: state.basics[summaryId],
    payDetail: state.payDetails[summaryId]
  } : null;

const _getLiteCrew = (state: Startcard): LiteCrew => {
  if (state) {
    return {
      firstname: state.firstName,
      lastname: state.lastName,
      ssn: state.ssn || '',
      fein: '',
      corpName: state.corpName || '',
      crewType: state.crewType || '',
      jobCode: state.jobCode.value,
      scheduleCode: state.scheduleCode.value,
      union: state.union,
      jobTitle: state.jobTitle || '',
      workLocation: state.workLocation,
      residentLocation: state.residentLocation,
      department: state.department || '',
      epCode: state.epCode || '--',
      vacationType: state.vacationType,
      holidayType: state.holidayType,
      hiredInLocation: state.hiredInLocation
    };
  }
};
const _mapPunchToModelView = (payPunches: UsaPunch[], shiftPunchDates: ShiftDates, workDetails: CrewDetail[]): DiscrepancyViewModel => _
  .chain(shiftPunchDates)
  .groupBy((sDate) => sDate.date)
  .mapValues((values) => _
    .chain(values)
    .sortBy('splitShiftOrdinal')
    .map(({shiftId, date, splitShiftOrdinal}) => {
      const punch = _.find(payPunches, (payPuch) => payPuch.shiftId === shiftId);
      const workDetail = _.find(workDetails, (details) => details.shiftId === shiftId);
      return !_.isUndefined(splitShiftOrdinal) ?
        {
          punch,
          workDetail,
          date,
          splitShiftOrdinal
        } : {
          punch,
          workDetail,
          date
        };
    })
    .value()
  )
  .value();


/**
 * Mapper to make sure default/user added summaryLines are at the bottom (guarantee order of summaryItems)
 * refer commment in https://jira.ep.com/browse/STF-2564
 * @param {SummaryItems} state - SummaryItems State from ngrxStore
 * @param {string} sliceName - name of the storeslice to take
 */

const _summaryMapper = ( state: SummaryItems, sliceName: string) => {
  const summaryPartition = _.partition(state && state[sliceName], sliceObj => !_.startsWith(sliceObj.summaryId, fromCommonConstants.NewSummaryLineIdPrefix));
  return _
    .chain(summaryPartition)
    .flatten()
    .orderBy('ordinal')
    .value();
};

const _selectSummaryBasics = (state: SummaryItems): SummaryBasics[] => _summaryMapper(state, 'basics');
const _selectSummaryDates = (state: SummaryItems): SummaryDate[] => _summaryMapper(state, 'dates');
const _selectSummaryAccountCodes = (state: SummaryItems): SummaryAccountCode[] => _summaryMapper(state, 'accountCodes');
const _selectSummaryPayDetails = (state: SummaryItems): SummaryPayDetail[] => _summaryMapper(state, 'payDetails');
const _selectSummaryJobDetails = (state: SummaryItems): SummaryJobDetail[] => _summaryMapper(state, 'jobDetails');
const _buildColumnsFromPayFactor = (payFactorTotals: PayFactorTotal[]): GridColumn[] => {
  return _
    .chain(payFactorTotals)
    .map((payFactorTotal: PayFactorTotal) => {
      return {
        field: payFactorTotal.payFactor.toString(),
        header: `${payFactorTotal.payFactor}x`
      };
    })
    .value();
};
const _selectBreakageColumns = (state: UsaTimecard, punchTab: PunchTab): GridColumn[] => {
  const isSuppressBreakageMinutes = _.get(state, 'rawBreakages.suppressBreakageMinutesFlag', false);
  const defaultColumns = _.map(TimecardDetailConstants.TimecardEntry.BreakageColumnConfig.columns, (val) => ({field: val.field, header: val.header}));
  const payFactors = punchTab === PunchTab.Pay_Times ? _.get(state, 'breakageTotals.payFactors', []) : [];
  if (!payFactors || payFactors.length === 0 || isSuppressBreakageMinutes) {
    return defaultColumns;
  }
  return _.concat(_.take(defaultColumns, 2), _buildColumnsFromPayFactor(payFactors), _.takeRight(defaultColumns, 5));
};
const _selectAccountCodes = (state: UsaShifts): AccountCoding[] => _.map(state ? state.accountCodes : null, (accCode) => accCode);

const _buildDefaultShift = (startcard, projectAccountCodeConfig, initialDefaultShift) => {
  const defaultSegments: ValueError<string>[] =
    _.get(startcard, 'accountCodeSegments.length', 0) > 0
      ? _.map(startcard.accountCodeSegments, (segmentValue) => ({value: segmentValue.value, errorMessage: '', errorType: undefined}))
      : _
        .chain(projectAccountCodeConfig ? projectAccountCodeConfig.segments : [])
        .map((segment) => ({value: segment.default, errorMessage: '', errorType: undefined}) )
        .value();

  const defaultWorkLocation =  _.get(startcard, 'workLocation', {});
  return {
    ...initialDefaultShift,
    accountCode: {
      ...initialDefaultShift.accountCode,
      segments: defaultSegments
    },
    workDetail: {
      ...initialDefaultShift.workDetail,
      ...defaultWorkLocation,
      offProd: startcard?.offProduction,
      workCondition: {value: startcard?.condition},
      mealPenalty1: startcard?.mpv1 ?? MealPenaltyEnum.Yes,
      mealPenalty2: startcard?.mpv2 ?? MealPenaltyEnum.Yes,
      nightPremium: startcard?.nightPremium
    }
  };
};

const _selectTimecardBatch = (state: UsaTimecard): Batch => state ? state.batch : null;

// generate shift data to update shifts till friday to use for copyDownUptoFriday
function _selectUpdatedShiftsUptoFriday(targetShifts: ShiftDate[], shifts: UsaShifts, shiftId: string, srcSplitShiftOrdinal: number) {
  const shift = {};
  _.each(targetShifts, ({shiftId: targetId, splitShiftOrdinal, date}) => {
    // If copying down from a split shift and the target shift splitShiftOrdinal is 0, we just update the splitShiftOrdinal and skip rest
    if (srcSplitShiftOrdinal === 1 && splitShiftOrdinal === 0) {
      return _.setWith(shift, ['shiftDates', targetId], {...shifts.shiftDates[targetId], splitShiftOrdinal}, Object);
    }
    const shiftBreakage = !_.isUndefined(shifts.breakages?.[targetId])
      ? shifts.breakages?.[targetId]
      : {
        shiftId: targetId,
        hoursPaid: 0,
        hoursWorked: 0,
        MP1Count: 0,
        MP1Amount: 0,
        MP2Count: 0,
        MP2Amount: 0,
        totalAmount: 0,
        payFactors: _.map(shifts.breakages?.[shiftId]?.payFactors, ({payFactor, rate, payCode}: PayFactor) => ({
          payFactor,
          rate,
          payCode,
          duration: {value: 0}
        })),
        isRestViolation: false,
      };
    _.setWith(shift, ['workDetails', targetId], {...shifts.workDetails[shiftId], shiftId: targetId}, Object);
    _.setWith(shift, ['shiftRates', targetId], {...shifts.shiftRates?.[shiftId], shiftId: targetId}, Object);
    _.setWith(shift, ['payPunches', targetId], {...shifts.payPunches?.[shiftId], shiftId: targetId}, Object);
    _.setWith(shift, ['mobilePunches', targetId], {...shifts.mobilePunches?.[shiftId], shiftId: targetId}, Object);
    _.setWith(shift, ['prPunches', targetId], {...shifts.prPunches?.[shiftId], shiftId: targetId}, Object);
    _.setWith(shift, ['accountCodes', targetId], {...shifts.accountCodes?.[shiftId], shiftId: targetId}, Object);
    _.setWith(shift, ['shiftDates', targetId], {...shifts.shiftDates[targetId], date,  shiftId: targetId, splitShiftOrdinal}, Object);
    _.setWith(shift, ['workStatuses', targetId], {...shifts.workStatuses?.[shiftId], shiftId: targetId}, Object);
    _.setWith(shift, ['breakages', targetId], {...shiftBreakage}, Object);
  });
  return shift;
}

const _selectTargetShiftDatesAsArrayUptoFriday = (
  shiftDatesAsArray: ShiftDate[],
  fridayShiftDate: ShiftDate,
  focusedDate: Date,
  srcSplitShiftOrdinal: number = 0,
  isValidCopy: boolean
): ShiftDate[] => {
  return _
    .chain(shiftDatesAsArray)
    .thru(value => isValidCopy ? value : [])
    .filter(({date}: ShiftDate) => {
      const shiftDate = new Date(date);
      return focusedDate < shiftDate && shiftDate <= new Date(fridayShiftDate.date);
    })
    .map(({shiftId, date, splitShiftOrdinal}: ShiftDate) => {
      // Copying down is done from a split shift and there are no split shifts for that shift
      // return the new split shift to be created and existing normal shift with splitShiftOrdinal as 0.
      if (srcSplitShiftOrdinal === 1 && !IsSplitShift(_.keyBy(shiftDatesAsArray, 'shiftId'), date)) {
        return [{shiftId, date, splitShiftOrdinal: 0}, {shiftId: `${date}_1`, date, splitShiftOrdinal: 1}];
      }
      return [{shiftId, date, splitShiftOrdinal}];
    })
    .flatten()
    // If copying down from a normal shift, we filter the normal shifts as the target shifts.
    // If copying down from a  split shift, we filter all the    shifts as the target shifts.
    .filter(({splitShiftOrdinal = 0}) => srcSplitShiftOrdinal === 0 ? splitShiftOrdinal === srcSplitShiftOrdinal : true)
    .value();
};

const _findShifts = (splitShiftOrdinal: number, shiftDate: ShiftDate, date: string ): boolean => {
  if (splitShiftOrdinal === 0) {
    return shiftDate.date === date && (shiftDate.splitShiftOrdinal === splitShiftOrdinal || shiftDate.splitShiftOrdinal === undefined);
  }
  return shiftDate.date === date && shiftDate.splitShiftOrdinal === splitShiftOrdinal;
};

// if mobile time punches have at least one mobile punch, calculate discrepancies between pay time punches & mobile time punches
const _getPayTimeToMobileTimeDiscrepancies = (allPossibilities: PossibleShift[], orderedShifts, payTimeDates: ShiftDates , mobileDates: ShiftDates ,
                                              payTimePunches: UsaPunch[], mobileTimePunches: UsaPunch[]) => {
  const isMobileTimeExists = _.some(mobileTimePunches, (({shiftId}: UsaPunch) => IsValidUUID(shiftId)));
  if (!isMobileTimeExists) {
    return [];
  }
  return _
    .chain(allPossibilities)
    .map(({date, splitShiftOrdinal}) => {
      const payTimeShift = _.find(payTimeDates, (shiftDate) => _findShifts(splitShiftOrdinal, shiftDate, date));
      const externalShift = _.find(mobileDates, (shiftDate) => _findShifts(splitShiftOrdinal, shiftDate, date));
      const payPunch = _.find(payTimePunches, {shiftId: payTimeShift?.shiftId});
      const mobilePunch = _.find(mobileTimePunches, {shiftId: _.get(externalShift, 'shiftId', null)});
      if (payTimeShift) {
        return _.mapValues(payPunch, (punch, punchItemKey) => {
          const payPunchValue = _.get(payPunch, `${punchItemKey}.value`);
          const mobilePunchValue = _.get(mobilePunch, `${punchItemKey}.value`);
          const errorType =  _.get(payPunch, `${punchItemKey}.errorType`, null);
          if (!_.isEqual(mobilePunchValue, payPunchValue) && !_.isEqual(punchItemKey, Field.Last) && !_.includes([Field.Ndb1In, Field.Ndb2In], punchItemKey)) {
            return {
              value: mobilePunchValue,
              errorType,
              errorMessage: _.get(payPunch, `${punchItemKey}.errorMessage`, ''),
            };
          }
          return _.get(mobilePunch, `${punchItemKey}`);
        });
      }
      return payTimeShift;
    })
    .filter(item => !_.isUndefined(item))
    .value();
};

// To update the workDetails dayType1 value with description to be shown on Discrepancy modal
const _updateDayTypeValue = (detail: CrewDetail, dayTypes: DayType[]): CrewDetail => {
  const dayType = _.find(dayTypes, (type) => _.isEqual(type.dayTypeCode, detail.dayType1?.value));
  return {...detail, dayType1: {...detail.dayType1, value: dayType?.description || ''}};
};
const _mapPunches = (shifts: UsaShifts, weekEndingDate, weekStartOn: DayOfWeek): UsaPunch[] => {
  const orderedShifts = CalculateOrderedShifts(weekEndingDate, shifts?.shiftDates, weekStartOn);
  const payPunches = shifts?.payPunches;
  return _.map(orderedShifts, ({shiftId}) => payPunches[shiftId]);
};

const _getPunchesWithDiscrepancies = (allPossibilities: PossibleShift[], orderedShifts, payTimeDates: ShiftDates, externalDates: ShiftDates ,
                                      payTimePunches: UsaPunch[], externalTimePunches: UsaPunch[], externalTimePunchType: PunchType) => {
  return _
    .chain(allPossibilities)
    .map(({date, splitShiftOrdinal= 0}) => {
      const payTimeShift = _.find(payTimeDates, (shiftDate) => _findShifts(splitShiftOrdinal, shiftDate, date));
      const externalShift = _.find(externalDates, (shiftDate) => _findShifts(splitShiftOrdinal, shiftDate, date));
      const payPunch = _.find(payTimePunches, {shiftId: payTimeShift?.shiftId});
      const externalPunch = _.find(externalTimePunches, {shiftId: _.get(externalShift, 'shiftId', null)});
      if (payTimeShift) {
        return _.mapValues(payPunch, (punch, punchItemKey) => {
          const payPunchValue = _.get(payPunch, `${punchItemKey}.value`, undefined);
          const externalTimePunchValue = _.get(externalPunch, `${punchItemKey}.value`, undefined);
          if (_.get(payPunch, `${punchItemKey}.errorType`, null) === ErrorType.DISCREPANCY &&
            !_.isEqual(externalTimePunchValue, payPunchValue) && _.keys(externalPunch).length > 1 &&
            !(_.isEqual(externalTimePunchType, PunchType.MobileTimePunches) && _.isEqual(punchItemKey, Field.Last))) {
            return {
              value: externalTimePunchValue,
              errorType:  _.get(payPunch, `${punchItemKey}.errorType`, null),
              errorMessage: _.get(payPunch, `${punchItemKey}.errorMessage`, ''),
            };
          }
          return _.get(externalPunch, `${punchItemKey}`, undefined);
        });
      }
      return payTimeShift;
    })
    .filter(item => !_.isUndefined(item))
    .value();
};

const _getDetailsWithDiscrepancies = (allPossibilities: PossibleShift[], payTimeDates: ShiftDates,
                                      externalDates: ShiftDates, payTimeDetails: CrewDetail[], externalTimeDetails: CrewDetail[]): CrewDetail[] => {
  return _
    .chain(allPossibilities)
    .map(({date, splitShiftOrdinal}) => {
      const payTimeShift = _.find(payTimeDates, (shiftDate) => _findShifts(splitShiftOrdinal, shiftDate, date));
      const externalShift = _.find(externalDates, (shiftDate) => _findShifts(splitShiftOrdinal, shiftDate, date));
      const workDetail = _.find(payTimeDetails, {shiftId: payTimeShift?.shiftId});
      const externalDetail = _.find(externalTimeDetails, {shiftId: _.get(externalShift, 'shiftId', null)});
      if (payTimeShift) {
        return _.mapValues(workDetail, (punch, detailItemKey) => {
          const externalTimeDetailValue = _.get(externalDetail, `${detailItemKey}.value`, undefined);
          const payTimeDetailValue = _.get(workDetail, `${detailItemKey}.value`, undefined);
          const hasDiscrepancy = _.get(workDetail, `${detailItemKey}.errorType`, null) === 'DISCREPANCY';
          if (hasDiscrepancy && !_.isEqual(externalTimeDetailValue, payTimeDetailValue)) {
            return {
              value: externalTimeDetailValue,
              errorType:  _.get(workDetail, `${detailItemKey}.errorType`, null),
              errorMessage: _.get(workDetail, `${detailItemKey}.errorMessage`, ''),
            };
          }
          return _.get(externalDetail, `${detailItemKey}`, undefined);
        });
      }
      return undefined;
    })
    .filter(item => !_.isUndefined(item))
    .value();
};
const _getMobileDetailsWithDiscrepancies = (allPossibilities: PossibleShift[], payTimeDates: ShiftDates,
                                            externalDates: ShiftDates, payTimeDetails: CrewDetail[], externalTimeDetails: CrewDetail[]): CrewDetail[] => {
  return _
    .chain(allPossibilities)
    .map(({date, splitShiftOrdinal}) => {
      const payTimeShift = _.find(payTimeDates, (shiftDate) => _findShifts(splitShiftOrdinal, shiftDate, date));
      const externalShift = _.find(externalDates, (shiftDate) => _findShifts(splitShiftOrdinal, shiftDate, date));
      const workDetail = _.find(payTimeDetails, {shiftId: payTimeShift?.shiftId});
      const externalDetail = _.find(externalTimeDetails, {shiftId: _.get(externalShift, 'shiftId', null)});
      if (payTimeShift) {
        return _.mapValues(workDetail, (punch, detailItemKey) => {
          const externalTimeDetailValue = _.get(externalDetail, `${detailItemKey}.value`, undefined);
          if (_.get(workDetail, `${detailItemKey}.errorType`, null) === 'DISCREPANCY') {
            return {
              value: externalTimeDetailValue,
              errorType:  _.get(workDetail, `${detailItemKey}.errorType`, null),
              errorMessage: _.get(workDetail, `${detailItemKey}.errorMessage`, ''),
            };
          }
          return _.get(externalDetail, `${detailItemKey}`, undefined);
        });
      }
      return undefined;
    })
    .filter(item => !_.isUndefined(item))
    .value();
};

const _columnValueChanges = (modalLines: FlatSummaryLine[]) => {
  const stCalculatedLine: FlatSummaryLine = modalLines[0];
  const userLine: FlatSummaryLine = modalLines[1];
  return _.reduce(stCalculatedLine, (difference, stCalcValue, stCalcKey) => {
    if (stCalcKey === 'segments') {
      difference[stCalcKey] = _.map(<ValueError<string>[]>stCalcValue, (accCode, idx) =>
        _.isEqual(accCode,  userLine[stCalcKey][idx]));
      return difference;
    }
    const isEqual = _.isEqual(stCalcValue, userLine[stCalcKey]);
    difference[stCalcKey] = {isEqual};
    return difference;
  }, {});
};

const _selectOrderedShiftIds = (weekEndingDate: string, shifts: UsaShifts, {payrollWeekDisplay}: ProjectSetting, weekStartsOn: DayOfWeek) => {
  const sortedWeekDays = CalculateOrderedWeekDays(weekEndingDate, weekStartsOn);
  const orderedShifts = CalculateOrderedShifts(weekEndingDate, shifts?.shiftDates, weekStartsOn);
  const splicedWeekDays = sortedWeekDays.splice(0, payrollWeekDisplay);
  const orderedWeekDays = _.concat(sortedWeekDays, splicedWeekDays);
  const numberOfShiftsToBeRemoved = _.findIndex(orderedShifts, ({date}) => date === format(orderedWeekDays[0], StandardDateFormatString1));
  const splicedShifts = orderedShifts.splice(0, numberOfShiftsToBeRemoved);

  return _.map(_.concat(orderedShifts, splicedShifts), (({date, shiftId, splitShiftOrdinal}) => {
    const index = _.findIndex(orderedWeekDays, (weekDay) => format(weekDay, StandardDateFormatString1) === date);
    return {
      shiftId,
      color: index % 2 === 0 ? '#e7f3fc' : '',
      borderBottom: splitShiftOrdinal === 0 ? '1px solid #e5e5e5' : 'none'
    };
  }));
};
const _selectAccountCodingSegmentsByShiftId = _.memoize((shiftId: string) => createSelector(
  selectAccountCodes,
  (accountCoding: AccountCoding[]) => accountCoding && shiftId && _.find(accountCoding, (item) => item.shiftId === shiftId)
));
// store.timecard.timecardEntry
export const selectTimeCardEntryRootState = createSelector(
  _selectTimecardFeatureState,
  _selectTimecardEntryState
);
// store.timecard.timecardEntry.timecard
export const selectTimecard = createSelector(
  selectTimeCardEntryRootState,
  _selectTimecard
);
export const selectTimecardId = createSelector(
  selectTimeCardEntryRootState,
  _selectTimecardId
);

export const selectWeekEndingDate = createSelector(
  selectTimecard,
  _selectWeekEndingDate
);

export const selectTimecardCreatedBy = createSelector(
  selectTimecard,
  (state) => state && state.createdBy
);

export const selectTimecardRevisedTimestamp = createSelector(
  selectTimecard,
  (state: UsaTimecard) => state && state.revisedTimestamp
);

export const selectTimecardRevisedBy = createSelector(
  selectTimecard,
  (state: UsaTimecard) => state && state.revisedBy
);

export const selectStartcard = createSelector(
  selectTimecard,
  _selectStartcard
);

export const selectStartcardId = createSelector(
  selectStartcard,
  _selectStartcardId
);
export const selectIsStartcardPayRateBelowScaleRate = createSelector(
  selectTimecardId,
  selectStartcard,
  (timecardId: string, startcard: Startcard) => startcard && startcard.isPayRateBelowScaleRate && !_.isUndefined(timecardId)
);
/**
 * STF-27086 : Returns timecard validation error for rate change banner when
 * timecard.timecardId is not empty i.e; we have a timecard object loaded onto store (to make sure the banner shows only on WTC screen and not on New TC screen)
 * and timecard.startcard.rateChangedRecently flag = true
 */
export const selectTimecardRateChangedBannerError = createSelector(
  selectTimecardId,
  selectStartcard,
  (timecardId: string, startcard: Startcard): ValidationError[] => !_.isEmpty(timecardId) && startcard && startcard.rateChangedRecently
    ? [{area: Area.Timecard, field: Field.ErrorBanner, message: ErrorMessages.StartcardRateChange, errorType: ErrorType.SUBMITTABLE, entityId: ''}]
    : []
);
export const selectTcShifts = createSelector(
  selectTimecard,
  _selectTcShifts
);
export const selectTcShiftDays = createSelector(
  selectTcShifts,
  _selectShiftDays
);
export const selectShifts = createSelector(
  selectTimecard,
  selectDailyTimeEntries,
  selectMobileTimeEntries,
  selectSelectedPunchTab,
  _selectShifts
);

export const selectAccountCodes = createSelector(
  selectShifts,
  _selectAccountCodes
);

export const selectShiftDays = createSelector(
  selectShifts,
  _selectShiftDays
);

export const selectMobileShiftDays = createSelector(
  selectMobileTimeEntries,
  _selectShiftDays
);
// returns the friday first shift, if copying down from source first shift and vice versa
export const selectFriday = _.memoize((shiftId: string) => createSelector(
  selectShiftDays,
  selectWeekEndingDate,
  selectRegionWeekStartsOn,
  (shiftDates: ShiftDate[], weekEndingDate: string, weekStartsOn: DayOfWeek): ShiftDate => {
    const orderedWeekDays = CalculateOrderedWeekDays(weekEndingDate, weekStartsOn);
    const fridayDate = _.find(orderedWeekDays, (date) => date.getDay() === DayOfWeek.Friday);
    const fridayShift = _
      .chain(shiftDates)
      .filter(({date}) => format(fridayDate, StandardDateFormatString) === date)
      .value();
    const noOfFridayShifts = _.size(fridayShift);
    const srcSplitShiftOrdinal = _.find(shiftDates, {shiftId})?.splitShiftOrdinal;
    return noOfFridayShifts > 1 && srcSplitShiftOrdinal === 1
      ? _.find(fridayShift, {splitShiftOrdinal: noOfFridayShifts - 1})
      : fridayShift[0];
  }
));

export const selectShiftDateById = (shiftId: string) => createSelector(
  selectShifts,
  (shifts) => shifts && shifts.shiftDates && new Date(shifts.shiftDates[shiftId].date)
);

export const selectMobileShiftDates = createSelector(
  selectMobileShiftDays,
  (mobileShiftDays: ShiftDate[]) => _.filter(mobileShiftDays, shiftDay => _.has(shiftDay, 'mobileTimeId'))
);

export const selectMobileShiftDatesUnique = createSelector(
  selectMobileShiftDays,
  (mobileShiftDays: ShiftDate[]) => _.filter(mobileShiftDays, shiftDay => _
    .has(shiftDay, 'mobileTimeId') &&  (shiftDay.splitShiftOrdinal === 0 || shiftDay.splitShiftOrdinal === undefined))
);

export const selectShiftDaysUnique = createSelector(
  selectShiftDays,
  (shiftDays: ShiftDate[]) => _.filter(shiftDays, shiftDay => (shiftDay.splitShiftOrdinal === 0 || shiftDay.splitShiftOrdinal === undefined))
);

export const selectIsValidCopyUptoFri = (shiftId: string) => createSelector(
  selectShiftDateById(shiftId),
  selectProjectSettings,
  (date, {payrollWeekDisplay}) => {
    const day = date.getDay();
    return (payrollWeekDisplay === DayOfWeek.Sunday && day === DayOfWeek.Sunday)
      || (day !== DayOfWeek.Friday && day !== DayOfWeek.Saturday && day !== DayOfWeek.Sunday);
  }
);

export const selectFridayShiftId = _.memoize((shiftId: string) => createSelector(
  selectFriday(shiftId),
  (state) => state && state.shiftId
));

export const selectTargetShiftDatesAsArrayUptoFriday = (shiftId: string, splitShiftOrdinal: number) => createSelector(
  selectShiftDays,
  selectShiftDateById(shiftId),
  selectIsValidCopyUptoFri(shiftId),
  selectFriday(shiftId),
  (shiftDays, focusedShiftDate, isValidCopy, friday) =>
    _selectTargetShiftDatesAsArrayUptoFriday(shiftDays, friday, focusedShiftDate, splitShiftOrdinal, isValidCopy)
);

export const selectUpdatedShiftsUptoFriday = (srcShiftId: string, srcSplitShiftOrdinal: number) => createSelector(
  selectTargetShiftDatesAsArrayUptoFriday(srcShiftId, srcSplitShiftOrdinal),
  selectShifts,
  (targetShifts, shifts) => _selectUpdatedShiftsUptoFriday(targetShifts, shifts, srcShiftId, srcSplitShiftOrdinal)
);

export const selectPayPunches = createSelector(
  selectShifts,
  _selectPayPunches
);

export const selectMobilePayPunches = createSelector(
  selectMobileTimeEntries,
  _selectPayPunches
);

export const selectMobileApprovalByShiftId = (shiftId: string) => createSelector(
  selectMobileTimeEntries,
  selectMobileShiftsWithDiscrepancy,
  selectMobileApprovalLoops,
  ({approvals}: MobileShifts, shiftDates: ShiftDate[], mobileApprovalLoops: MobileApprovalLoop[]): MobileApprovalCircle[] => {
    const {
      deptAdminApprovalId,
      deptAdminApprovalStatus = MobileApprovalStatus.PENDING,
      deptAdminApprovalFirstName,
      deptAdminApprovalLastName,
      crewApprovalId,
      crewApprovalStatus = MobileApprovalStatus.PENDING,
      crewApprovalFirstName,
      crewApprovalLastName
    } = approvals[shiftId];
    const hasMobileDiscrepancy = _.some(shiftDates, { mobileTimeId: shiftId }) && !_.isEmpty(mobileApprovalLoops);
    return [{
      approverId: crewApprovalId,
      status: crewApprovalStatus,
      circleDisplay: `${_.toUpper(_.head(crewApprovalFirstName)) || '-'}${_.toUpper(_.head(crewApprovalLastName)) || '-'}`,
      hasMobileDiscrepancy
    }, {
      approverId: deptAdminApprovalId,
      status: deptAdminApprovalStatus,
      circleDisplay: `${_.toUpper(_.head(deptAdminApprovalFirstName)) || '-'}${_.toUpper(_.head(deptAdminApprovalLastName)) || '-'}`,
      hasMobileDiscrepancy
    }];
  }
);

export const selectShiftRates = createSelector(
  selectShifts,
  _selectShiftRates
);

export const selectDetails = createSelector(
  selectShifts,
  selectActiveTimecardType,
  _selectDetails
);

export const selectBreakages = createSelector(
  selectShifts,
  _selectBreakages
);
export const selectPayTimesBreakages = createSelector(
  selectTcShifts,
  (state: UsaShifts): Breakages | null => state ? state.breakages : null
);
export const selectBreakageTotals = createSelector(
  selectTimecard,
  selectSelectedPunchTab,
  _selectBreakageTotal
);

export const selectBreakageColumns = createSelector(
  selectTimecard,
  selectSelectedPunchTab,
  _selectBreakageColumns
);

export const selectSummaryItems = createSelector(
  selectTimecard,
  _selectSummaryItems
);

export const selectPreviousSummaryValues = createSelector(
  selectSummaryItems,
  _selectSummaryPreviousValues
);

export const selectPreviousFlatSummaryLineValues = (summaryId: string) => createSelector(
  selectSummaryItems,
  (summaryItems: SummaryItems): FlatSummaryLine => {
    const isLineWithPrevHTgCal = (!summaryItems.basics[summaryId].isHTGItem && !summaryItems.basics[summaryId].isStartcardAllowance &&
      !summaryItems.basics[summaryId].isFLSAItem) || summaryItems.basics[summaryId].isShieldItem || !_.isEmpty(summaryItems.previousValues[`${summaryId}`]);

    return isLineWithPrevHTgCal
      ? <FlatSummaryLine>{}
      : {
        ...summaryItems.dates[summaryId],
        ...summaryItems.basics[summaryId],
        ...summaryItems.accountCodes[summaryId],
        ...summaryItems.jobDetails[summaryId],
        ...summaryItems.payDetails[summaryId]
      };
  }
);
export const selectProcessedMobileShifts = createSelector(
  selectTimeCardEntryRootState,
  (state: TimecardEntryState) => state ? state.processedMobileShifts : []
);
export const selectSummaryLineChangeHistory = createSelector(
  selectChangedSummaryLineId,
  selectPreviousSummaryValues,
  selectSummaryItems,
  (summaryId: string, previousLineValues: PreviousSummaryValues, summary: SummaryItems): SummaryLineChanges => {
    if (_.isEmpty(summaryId)) {
      return <SummaryLineChanges>{};
    }
    const currentSummaryLineDate: string = _.get(summary, `dates[${summaryId}].date.value`, '');
    const formattedDate = moment.utc(currentSummaryLineDate, StandardDateFormatString).format(StandardDateFormatString);
    const modalLines: FlatSummaryLine[] = summary.basics[summaryId].isShieldItem &&
    (summary.basics[summaryId].isHTGItem && previousLineValues[summaryId] ||
      (summary.basics[summaryId].isStartcardAllowance && !summary.basics[summaryId].isUserItem) ||
      summary.basics[summaryId].isFLSAItem)
      ? [
        {...previousLineValues[summaryId].prevHtgSummaryLine},
        {
          ...{date: {value: formattedDate}},
          ...summary.basics[summaryId],
          ...summary.accountCodes[summaryId],
          ...summary.jobDetails[summaryId],
          ...summary.payDetails[summaryId]
        }
      ]
      : [];
    const changes = _columnValueChanges(modalLines);
    return {
      user: previousLineValues[summaryId] ? previousLineValues[summaryId].user : '',
      date: formattedDate,
      modalLines,
      changes
    };
  }
);

export const selectSummaryBasics = createSelector(
  selectSummaryItems,
  _selectSummaryBasics
);

export const selectSummaryDates = createSelector(
  selectSummaryItems,
  _selectSummaryDates
);

export const selectSummaryShields = createSelector(
  selectSummaryBasics,
  ((basics: SummaryBasics[]) => _.map(basics, basic => basic.isShieldItem))
);

export const selectShieldedSummaryIds = createSelector(
  selectSummaryBasics,
  (shieldedSummaryBasics: SummaryBasics[]): string[] => {
    return _
      .chain(shieldedSummaryBasics)
      .map(({isShieldItem, isFLSAItem, summaryId}: SummaryBasics) => (isShieldItem && !isFLSAItem) ? summaryId : '')
      .filter((summaryId) => !_.isEmpty(summaryId))
      .value();
  }
);

export const selectSummaryJobDetails = createSelector(
  selectSummaryItems,
  _selectSummaryJobDetails
);
export const selectShift = createSelector(
  selectShifts,
  fromTimecardEntryUISelectors.selectClipboardShiftId,
  selectActiveTimecardType,
  _selectShift
);

export const selectSummaryPayDetails = createSelector(
  selectSummaryItems,
  _selectSummaryPayDetails
);

export const selectSummaryAccountCodes = createSelector(
  selectSummaryItems,
  _selectSummaryAccountCodes
);

export const selectNewTimecardEntryRouteMetadata = createSelector(
  selectSelectedProjectId,
  selectWeekEndingDate,
  selectStartcard,
  selectTimecardId,
  (projectId, weekEndingDate, startcard, timecardId) => ({projectId, weekEndingDate, startcard, timecardId})
);

export const selectSaveTimecardParams = createSelector(
  selectTimecard,
  selectSelectedProject,
  (timecard: UsaTimecard, project: Project) => ({timecard, startcardId: timecard.startcard.startcardId, projectId: project.projectId})
);

export const selectSummaryLine = createSelector(
  selectSummaryItems,
  fromTimecardEntryUISelectors.selectClipboardSummaryLineId,
  _selectSummaryLine
);

export const selectShiftIds = createSelector(
  selectShiftDays,
  _selectShiftIds
);

export const selectTimecardErrors = createSelector(
  selectTimecard,
  _selectTimecardErrors
);

// TODO: This is a quick hacky fix , it should be fixed from node side. Will do as part of https://jira.ep.com/browse/STF-24986
export const selectTimecardBannerErrors = createSelector(
  selectTimecardErrors,
  (errors: ValidationError[]) => errors && _
      .chain(errors)
      .filter((error) => error.field === fromCommonConstants.Field.ErrorBanner)
      .uniqBy((obj) => obj.message)
      .value()
);
export const selectTimecardSummaryErrorBanner = createSelector(
  selectTimecard,
  (timecard: Timecard): boolean => timecard && timecard.showUnsubmillableLowerBanner
);


export const selectDefaultShift = createSelector(
  selectStartcard,
  fromTimecardEntryUISelectors.selectAccountCodeConfig,
  (startcard, accountCodeConfig: AccountCodeConfig) => {
    return _buildDefaultShift(startcard, accountCodeConfig, TimecardDetailConstants.TimecardEntry.InitialDefaultShift);
  }
);

export const selectSagDefaultShift = createSelector(
  selectStartcard,
  fromTimecardEntryUISelectors.selectAccountCodeConfig,
  (startcard, accountCodeConfig: AccountCodeConfig) => {
    return _buildDefaultShift(startcard, accountCodeConfig, SagTimecardDetailConstants.TimecardEntry.SagInitialDefaultShift);
  }
);

export const selectIsLayoffTimecard = createSelector(
  selectTimecard,
  (timecard: UsaTimecard) => timecard ? timecard.isLayoffTimecard : false
);

export const selectIsWeekendRestOverride = createSelector(
  selectTimecard,
  (timecard: UsaTimecard) => timecard ? timecard.isWeekendRestOverride : false
);

export const selectTimecardBatch = createSelector(
  selectTimecard,
  _selectTimecardBatch
);

export const selectIsLockedTimecard = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): boolean => timecard
    ? _.includes([ApiInvoiceStatus.ENABLED_FOR_IPS, ApiInvoiceStatus.RELEASED, ApiInvoiceStatus.PROCESSED], timecard?.invoiceStatus)
    || timecard?.batch?.status === BatchStatus.PROCESSED
    : false
);

export const selectIsTimecardSubmitted = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): boolean => timecard ? timecard?.status === TimecardStatus.S || timecard?.batch?.status === 'SUBMITTED' : null
);

export const selectIsTimecardProcessed = createSelector(
  selectTimecardBatch,
  (state: Batch) => state ? state.status === 'PROCESSED' : null
);

export const selectIsTimecardRevised = createSelector(
  selectTimecard,
  (state: UsaTimecard) => state ? state.isRevised : false
);

export const selectIsTimecardOpen = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): boolean => timecard ? timecard?.status === TimecardStatus.O : false
);

export const selectIsShortCutKeysDisabled = createSelector(
  selectIsLockedTimecard,
  selectIsWorkLocationPickerOpened,
  (isLockedTimecard, IsWorkLocationPickerOpened) => {
    return isLockedTimecard && IsWorkLocationPickerOpened;
  }
);


export const selectTimecardShiftAndSummary = createSelector(
  selectShifts,
  selectSummaryItems,
  (shifts, summaryItems) => ({shifts, summaryItems})
);

export const selectCheckComment = createSelector(
  selectTimecard,
  (timecard: UsaTimecard) => timecard ? timecard.checkComment : ''
);

export const selectWorkDate = (shiftId: string) => createSelector(
  selectShiftDays,
  (dates) => _.find(dates, (date) => date.shiftId === shiftId)
);
export const selectWorkConditionByShiftId = (shiftId: string) => createSelector(
  selectDetails,
  (details: CrewDetail[]): CrewDetail => _.find(details, (detail: CrewDetail) => detail.shiftId === shiftId)
);
export const selectSummaryBasicBySummaryId = (summaryId: string) => createSelector(
  selectSummaryItems,
  (state: SummaryItems): SummaryBasics => state && state.basics[summaryId]
);
export const selectApprovalTasks = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): ApprovalTask[] => timecard ? timecard.approvalTasks : []
);
/**
 * This returns an ApprovalTask if the next available approvalTasks has the same role as mine for this project
 * */
export const selectMyApprovalTask = createSelector(
  selectApprovalTasks,
  selectProjectRoleId,
  selectProjectUserWorkflowIds,
  GetApprovalTaskByRole
);

export const selectIsUnsubmittableTimecard = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): boolean => timecard && timecard.isUnsubmittable
);
export const selectShowIsUnsubmittableTopBanner = createSelector(
  selectTimecard,
  (timecard: Timecard): boolean => timecard && timecard.showUnsubmillableTopBanner
);

export const selectHasOnlyCheckCommentsError = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): boolean => timecard && timecard.hasOnlyCheckCommentsError
);
export const selectIsShowTimecardErrorBanner = createSelector(
  selectIsUnsubmittableTimecard,
  selectHasOnlyCheckCommentsError,
  (isUnsubmittable: boolean, hasOnlyCheckCommentsError:  boolean): boolean =>
    isUnsubmittable && !hasOnlyCheckCommentsError
);

export const selectIsApprovalDisabled = createSelector(
  selectTimecardEntryDirtyStatus,
  selectIsUnsubmittableTimecard,
  selectHasOnlyCheckCommentsError,
  selectMyApprovalTask,
  (isDirty:  boolean, isUnsubmittable:  boolean, hasOnlyCheckCommentsError:  boolean, myApprovalTask: ApprovalTask|null) => {
    return isDirty || (isUnsubmittable && !hasOnlyCheckCommentsError) || myApprovalTask === null;
  }
);

export const selectIsTimecardHasApprovedTasks = createSelector(
  selectApprovalTasks,
  (approvalTasks: ApprovalTask[]) => {
    return _.some(approvalTasks, (approvalTask: ApprovalTask) => approvalTask.status === ApprovalStatus.COMPLETED);
  }
);

export const selectIsDisplayOrangeCircles = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): boolean => timecard && timecard.isDisplayOrangeCircle
);

export const selectBreakageTotalHoursPaid = createSelector(
  selectBreakageTotals,
  (breakageTotals: BreakageTotals) => breakageTotals && breakageTotals.hoursPaid ?  breakageTotals.hoursPaid : null
);

export const selectShiftsWithDiscrepancy = createSelector(
  selectTimecard,
  selectDailyTimeEntries,
  selectMobileTimeEntries,
  selectCrewDayType1LookupData,
  selectProcessedMobileShifts,
  selectRegionWeekStartsOn,
  (timecard: UsaTimecard, dtShifts: UsaShifts, mtShifts: MobileShifts, dayTypes: DayType[], processedMobileShifts, weekStartsOn: DayOfWeek) => {
    const shifts = _.get(timecard, 'shifts');
    const {payTimeToMobileTimeDiscrepancies, hasMobileTimeDiscrepancy} = GetShiftDiscrepancies(shifts, dtShifts, mtShifts, processedMobileShifts);
    const orderedShifts = CalculateOrderedShifts(timecard?.weekEndingDate, shifts?.shiftDates, weekStartsOn);
    const allPossibleShifts = GetAllPossibleShifts(orderedShifts);
    const dtPunches: UsaPunch[] = _mapPunches(dtShifts, timecard?.weekEndingDate, weekStartsOn);
    const payTimePunches: UsaPunch[] = _mapPunches(shifts, timecard?.weekEndingDate, weekStartsOn);
    const mtPunches: UsaPunch[] = _mapPunches(mtShifts, timecard?.weekEndingDate, weekStartsOn);
    const payTimeWorkDetails: CrewDetail[] = _.map(shifts && shifts.workDetails, detail => _updateDayTypeValue(GetCrewDetail(detail), dayTypes));
    const dtWorkDetails: CrewDetail[] = _.map(dtShifts && dtShifts.workDetails, detail => _updateDayTypeValue(GetCrewDetail(detail), dayTypes));
    const mtWorkDetails: CrewDetail[] = _.map(mtShifts && mtShifts.workDetails, detail => _updateDayTypeValue(GetCrewDetail(detail), dayTypes));
    const dailyTimePunches: UsaPunch[] = _getPunchesWithDiscrepancies(allPossibleShifts, orderedShifts,
      shifts?.shiftDates, dtShifts?.shiftDates, payTimePunches, dtPunches, PunchType.DailyTimePunches);
    const dailyTimeWorkDetails: CrewDetail[] =  _.some(dtWorkDetails, ({shiftId}) => IsValidUUID(shiftId))
      ? _getDetailsWithDiscrepancies(allPossibleShifts, shifts?.shiftDates, dtShifts?.shiftDates, payTimeWorkDetails, dtWorkDetails)
      : [];
    const hasMobilePunches = _.some(mtPunches, (punch) => HasPayPunchFields(punch));
    const hasMobileWorkDetails = _.some(mtWorkDetails, (detail) => _.has(detail, 'dayType1'));
    const mobileTimePunches: UsaPunch[] = (hasMobilePunches || hasMobileWorkDetails)
      ? _getPayTimeToMobileTimeDiscrepancies(allPossibleShifts, orderedShifts,
        shifts?.shiftDates, mtShifts?.shiftDates, payTimePunches, mtPunches)
      : _getPunchesWithDiscrepancies(allPossibleShifts, orderedShifts, shifts?.shiftDates,
        mtShifts?.shiftDates, payTimePunches, mtPunches, PunchType.MobileTimePunches);
    const mobileWorkDetails: CrewDetail[] = _.some(mtWorkDetails, ({shiftId}) => IsValidUUID(shiftId))
      ? _getMobileDetailsWithDiscrepancies(allPossibleShifts, shifts?.shiftDates, mtShifts.shiftDates, payTimeWorkDetails, mtWorkDetails)
      : [];
    const payPunchViewModel = _mapPunchToModelView(payTimePunches, shifts?.shiftDates, payTimeWorkDetails);
    const mobilePunchViewModel = _mapPunchToModelView(mobileTimePunches, mtShifts?.shiftDates, mobileWorkDetails);
    const dailyTimePunchViewModel = _mapPunchToModelView(dailyTimePunches, dtShifts?.shiftDates, dailyTimeWorkDetails);
    return {
      payPunchViewModel,
      dailyTimePunchViewModel,
      mobilePunchViewModel,
      payTimeWorkDetails,
      dtWorkDetails,
      mobileWorkDetails,
      mobileTimePunches,
      payTimeToMobileTimeDiscrepancies,
      hasMobileTimeDiscrepancy
    };
  }
);

export const selectIsDiscrepancy = createSelector(
  selectTimecard,
  (timecard: UsaTimecard) => {
    const {shifts} = timecard || {};
    return shifts && shifts.payPunches
      ? _.some(shifts.payPunches, (punch) => !!_.find(punch, {errorType: ErrorType.DISCREPANCY})) ||
      _.some(shifts.workDetails, (detail) => !!_.find(detail, {errorType: ErrorType.DISCREPANCY}))
      :  false;
  }
);

export const selectIsApplyOtherDataSource = createSelector(
  selectTimecard,
  (timecard: UsaTimecard) => timecard && timecard.isApplyOtherDataSource
);

export const selectIsMobileTimecard = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): boolean => timecard && timecard.isMobileTimecard
);

export const selectPaymasterEditedSummaryIds = createSelector(
  selectSummaryBasics,
  selectUserRole,
  (summaryBasics: SummaryBasics[], userRole: string) =>
    (_.toUpper(userRole) === InternalUserRole.PAY_MASTER || _.toUpper(userRole) === InternalUserRole.PAY_MASTER_MANAGER)
      ? []
      : _
        .chain(summaryBasics)
        .filter(({isPaymasterEditedLineItem}: SummaryBasics) => isPaymasterEditedLineItem)
        .map(({summaryId}: SummaryBasics) => summaryId)
        .value()
);

export const selectBlankSummaryLineIndex = createSelector(
  selectSummaryDates,
  (state: SummaryDate[]) => state.length - 1
);

export const selectIsTimeCardInternal = createSelector(
  selectTimecard,
  (state: UsaTimecard) => state && state.isInternal
);
export const selectPermissionTypeRequiredForTimecardAccess = createSelector(
  selectIsTimeCardInternal,
  selectPermissions,
  (isInternal: boolean, permissions: PermissionMap): Permission =>
    isInternal ? permissions[PermissionType.TIMECARD_INTERNAL] : permissions[PermissionType.TIMECARD_EXTERNAL]
);

export const permissionToEnableTCDetails = createSelector(
  selectPermissions,
  (permissions: PermissionMap) => !!permissions ? permissions[PermissionType.TIMECARD] === Permission.FULL : false
);
export const selectPermissionToEnableReport = createSelector(
  selectPermissions,
  (permissions: PermissionMap) => {
    return !!permissions ?
      permissions[PermissionType.REPORTS] === Permission.FULL &&
      (permissions[PermissionType.TIMECARD] === Permission.FULL || permissions[PermissionType.TIMECARD] === Permission.READ) : false;
  }
);

export const selectHasReviseTcPermission = createSelector(
  selectUserRole,
  (userRole: string) => (_.toUpper(userRole) === InternalUserRole.PAY_MASTER || _.toUpper(userRole) === InternalUserRole.PAY_MASTER_MANAGER)
);

export const selectIsSummaryFilterEnabled = createSelector(
  permissionToEnableTCDetails,
  selectPermissionTypeRequiredForTimecardAccess,
  selectIsTimecardRevised,
  selectHasReviseTcPermission,
  selectIsTimecardOpen,
  selectIsTimeCardInternal,
  // Looks for Full Timecard Permission, user permission to timecard, and also if TC is submitted then check for revised and revisedTC permission
  (hasTimecardPermission: boolean, permissionTypeRequiredForTimecardAccess: Permission,
   isRevised: boolean, isPaymaster: boolean, isTimecardStatusOpen: boolean, isInternal: boolean) =>
    (hasTimecardPermission && permissionTypeRequiredForTimecardAccess === Permission.FULL && isTimecardStatusOpen)
    || (isPaymaster && isRevised && !isTimecardStatusOpen)
    || (!isInternal && isPaymaster && isTimecardStatusOpen)
);

export const selectIsRevisedTimecardMenuItemDisabled = createSelector(
  selectIsSavedTimecard,
  selectIsTimecardSubmitted,
  selectIsLockedTimecard,
  (isSaved: boolean, isSubmitted: boolean, isLocked: boolean) => !isSaved || !isSubmitted || isLocked
);

export const selectMobileShiftDetails = createSelector(
  selectShifts,
  (state: UsaShifts): MobileShiftNoteDetail[] => _
    .chain(state.shiftDates)
    .filter(({mobileTimeId}) => !_.isUndefined(mobileTimeId))
    .map(({date: shiftDate, mobileTimeId}) => ({shiftDate, mobileTimeId}))
    .value()
);

export const selectLiteCrew = createSelector(
  selectStartcard,
  _getLiteCrew
);

export const selectCalculatedTimecard = createSelector(
  selectTimecard,
  ({calculatedTimecard}): UsaTimecard|undefined => calculatedTimecard
);

export const selectDailyTimeDetails = createSelector(
  selectDailyTimeEntries,
  selectActiveTimecardType,
  _selectDetails
);

export const selectDailyTimePunches = createSelector(
  selectDailyTimeEntries,
  _selectPayPunches
);

export const selectShiftDates = createSelector(
  selectShifts,
  (shifts: UsaShifts) => shifts?.shiftDates
);

export const selectTCShiftDates = createSelector(
  selectTcShifts,
  (shifts: UsaShifts) => shifts?.shiftDates
);

export const selectOrderedShiftIds = createSelector(
  selectWeekEndingDate,
  selectShifts,
  selectProjectSettings,
  selectRegionWeekStartsOn,
  _selectOrderedShiftIds
);

export const selectOrderedTCShiftIds = createSelector(
  selectWeekEndingDate,
  selectTcShifts,
  selectProjectSettings,
  selectRegionWeekStartsOn,
  _selectOrderedShiftIds
);

export const selectOrderedTCShiftDates = createSelector(
  selectTCShiftDates,
  selectOrderedTCShiftIds,
  (shiftDates: ShiftDates, orderedShiftIds: { [key: string]: string }[]) => _.map(orderedShiftIds, ({shiftId}) => shiftDates[shiftId])
);

export const selectApprovalShiftDays = createSelector(
  selectOrderedTCShiftDates,
  selectDailyTimeEntries,
  selectTcShifts,
  (orderedShiftDates: ShiftDate[], dtShifts: UsaShifts, tcShifts: UsaShifts): ShiftDate[] => _.filter(orderedShiftDates, (shiftDate: ShiftDate) =>
    IsValidUUID(shiftDate?.mobileTimeId) ||
    (IsValidUUID(shiftDate.dailyTimeId) && !IsShiftTimesEmpty(
        dtShifts?.payPunches[shiftDate.dailyTimeId],
        GetCrewDetail(dtShifts?.workDetails[shiftDate.dailyTimeId]))
    ) ||
    !IsShiftTimesEmpty(tcShifts.payPunches[shiftDate.shiftId], GetCrewDetail(tcShifts.workDetails[shiftDate.shiftId]))
  ) ?? []
);

export const selectWTCPayPunches = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): UsaPunch[] => _.map(timecard.shifts.payPunches ?? null, (punch: UsaPunch) => punch)
);

export const selectIsTimecardEnabled = createSelector(
  selectIsTimecardSubmitted,
  selectHasReviseTcPermission,
  selectIsTimecardRevised,
  selectIsLockedTimecard,
  selectPermissionTypeRequiredForTimecardAccess,
  (isTimecardSubmitted: boolean, hasTimecardRevisePermission: boolean, isTimecardRevised: boolean,
   isTimecardLocked: boolean, tcActionsPermission: string) => {
    const isSubmitted = (isTimecardSubmitted && !(hasTimecardRevisePermission && isTimecardRevised));
    const hasTcActionPermission = ((tcActionsPermission === Permission.FULL) || (hasTimecardRevisePermission && isTimecardRevised));
    return  !(isSubmitted || isTimecardLocked) && hasTcActionPermission;
  }
);

export const selectIsTimecardEditActionsDisabled = createSelector(
  selectPermissionTypeRequiredForTimecardAccess,
  selectIsTimecardRevised,
  selectHasReviseTcPermission,
  selectIsTimecardOpen,
  selectIsTimecardSubmitted,
  selectIsLockedTimecard,
  (timeCardActionsPermission, isRevised, revisePermission, isTimecardStatusOpen,
   isSubmitted, isTCLocked) =>
    isSubmitted
    || !(timeCardActionsPermission === Permission.FULL || (revisePermission && isRevised && !isTimecardStatusOpen))
    || (isSubmitted && !(revisePermission && isRevised))
    || isTCLocked
);

export const selectMobileShiftsWithDiscrepancy = createSelector(
  selectShiftsWithDiscrepancy,
  selectShiftDays,
  ({payTimeToMobileTimeDiscrepancies}, shiftDates: ShiftDate[]): ShiftDate[] => _
    .chain(payTimeToMobileTimeDiscrepancies)
    .map(({entityId}) => {
      return _.find(shiftDates, (shift) => shift.shiftId === entityId);
    })
    .value()
);

export const selectUniqMobileShiftsWithDiscrepancy = createSelector(
  selectMobileShiftsWithDiscrepancy,
  (shiftDates: ShiftDate[]): ShiftDate[] => _.uniqBy(shiftDates, 'date')
);

export const selectHasMobilePunchOrWorkDiscrepancy = createSelector(
  selectShiftsWithDiscrepancy,
  ({hasMobileTimeDiscrepancy}): boolean => hasMobileTimeDiscrepancy
);

export const selectIsMobileApproved = createSelector(
  selectHasMobilePunchOrWorkDiscrepancy,
  selectMobileApprovalStatus,
  (hasMobileDiscrepancy: boolean, isMobileApproved: boolean): boolean => isMobileApproved && !hasMobileDiscrepancy
);

export const selectIsTimecardNotSubmitted = createSelector(
  selectIsTimecardSubmitted,
  (isTimecardSubmitted: boolean): boolean => !isTimecardSubmitted
);

export const selectRawBreakages = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): GrossPay => timecard && timecard.rawBreakages
);

export const selectIsSuppressBreakageMinutes = createSelector(
  selectRawBreakages,
  (rawBreakage: GrossPay): boolean => _.get(rawBreakage, 'suppressBreakageMinutesFlag', false)
);

export const selectBreakageOverrides = createSelector(
  selectShiftDates,
  selectOrderedShiftIds,
  selectPayTimesBreakages,
  selectRawBreakages,
  (shiftDates: ShiftDates, orderedShiftIds: { [key: string]: string }[], payTimeBreakages: Breakages, rawBreakages: GrossPay) =>
    MapToBreakageOverrideViewModel(shiftDates, orderedShiftIds, payTimeBreakages, rawBreakages)
);

export const selectSummaryLastIndex = createSelector(
  selectSummaryBasics,
  ((basics: SummaryBasics[]) => _.values(basics).length - 1)
);

export const selectWorkflowApprovalStatus = createSelector(
  selectTimecard,
  (state: UsaTimecard): WorkflowApprovalStatus => state && state.workflowApprovalStatus
);

export const selectCanReturnToCrew = createSelector(
  selectTimecardEntryDirtyStatus,
  selectIsUnsubmittableTimecard,
  selectHasOnlyCheckCommentsError,
  selectHasMobilePunchOrWorkDiscrepancy,
  selectApprovalTasks,
  selectMyApprovalTask,
  (isDirty:  boolean, isUnsubmittable:  boolean, hasOnlyCheckCommentsError:  boolean, hasMobileDiscrepancy: boolean,
   approvalTasks: ApprovalTask[], approvalTask: ApprovalTask|null): boolean =>
    !isDirty && hasMobileDiscrepancy && (!isUnsubmittable || hasOnlyCheckCommentsError) && (_.isEmpty(approvalTasks) || approvalTask !== null)
);

export const selectSplitShiftDetails = (shiftId: string) => createSelector(
  selectPayPunches,
  selectShiftRates,
  selectDetails,
  selectTcShiftDays,
  (punches: UsaPunch[], shiftRates: ShiftRate[], details: CrewDetail[],
   shiftDates: ShiftDate[]): {punch: UsaPunch, shiftRate: ShiftRate, detail: CrewDetail, shiftDate: string, splitShiftOrdinal: number} => ({
    punch: _.find(punches, {shiftId}),
    shiftRate: _.find(shiftRates, {shiftId}),
    detail: _.find(details, {shiftId}),
    shiftDate: _.get(_.find(shiftDates, {shiftId}), 'date', ''),
    splitShiftOrdinal: _.get(_.find(shiftDates, {shiftId}), 'splitShiftOrdinal', undefined)
  })
);

export const selectShiftSection = (sectionSlice: string): any => createSelector(
  selectShifts,
  shift => shift && shift[sectionSlice]
);

export const selectNonSplitShiftDates = createSelector(
  selectShifts,
  selectWeekEndingDate,
  selectOrderedShiftIds,
  (shifts: UsaShifts, weekEndingDate: string, orderedShiftIds: { [key: string]: string }[]): ShiftDate[] => {
    return _
      .chain(orderedShiftIds)
      .map(({shiftId}) => shifts.shiftDates[shiftId])
      .filter(({splitShiftOrdinal = 0 }) => splitShiftOrdinal === 0)
      .value();
  }
);
export const selectDiscrepancyModalSelectedDate = createSelector(
  selectNonSplitShiftDates,
  selectShiftsWithDiscrepancy,
  (shiftDates: ShiftDate[], {payPunchViewModel}): string => {
    const orderedPunches =  _
      .chain(shiftDates)
      .map(shiftDate => {
        return _.filter(_.flatten(_.values(payPunchViewModel)), (payPunch) => payPunch.date === shiftDate.date );
      })
      .flatMap()
      .value();
    return _
      .chain(orderedPunches)
      .filter((discrepancyItem: Discrepancy) => _
        .some(_.mapValues(discrepancyItem.punch), {errorType: ErrorType.DISCREPANCY}) || _
        .some(_.mapValues(discrepancyItem.workDetail), {errorType: ErrorType.DISCREPANCY}))
      .first()
      .value()
      .date;
  }
);
export const checkPaymasterExternalTimecardDisabled = createSelector(
  selectHasReviseTcPermission,
  selectIsTimeCardInternal,
  selectIsTimecardRevised,
  (isPaymaster: boolean, isInternal: boolean, isRevised: boolean) =>
    ((!isInternal && isPaymaster) || (isInternal && !isPaymaster)) && !isRevised
);
export const selectHasPAorGateKeeperPermission = createSelector(
  selectProjectPermissions,
  (projectPermissions: ProjectPermissions): boolean => {
    if (!projectPermissions || !projectPermissions.permissions) { return false; }
    if (UserIsInRole('GateKeeper', projectPermissions)) {
      return UserHasPermission(PermissionType.ROUTE_TO_APPROVERS, Permission.FULL, projectPermissions) &&
        UserHasPermissions(PermissionType.TIMECARD_APPROVALS, [Permission.READ, Permission.FULL], projectPermissions);
    }
    if (UserIsInRole('Payroll Accountant', projectPermissions)) {
      return UserHasPermissions(PermissionType.ROUTE_TO_APPROVERS, [Permission.READ, Permission.FULL], projectPermissions);
    }
    return false;
  }
);
export const selectHasApproverPermission = createSelector(
  selectProjectPermissions,
  (projectPermissions: ProjectPermissions): boolean => {
    if (!projectPermissions || !projectPermissions.permissions) { return false; }
    if (UserIsInRole('Production Accountant', projectPermissions) || UserIsInRole('Producer/UPM', projectPermissions)
      || UserIsInRole('GateKeeper', projectPermissions)) {
      return UserHasPermissions(PermissionType.TIMECARD_APPROVALS, [Permission.READ, Permission.FULL], projectPermissions);
    }
    return false;
  }
);
export const selectIsReturnToCrewHidden = createSelector(
  selectProjectPermissions,
  selectWorkflowApprovalStatus,
  selectIsMobileTimecard,
  (projectPermissions: ProjectPermissions, workflowApprovalStatus: WorkflowApprovalStatus, mobileTimecard: boolean): boolean =>
    !mobileTimecard ||
    !(UserIsInRole('Payroll Accountant', projectPermissions) ||
      (UserIsInRole('GateKeeper', projectPermissions) &&
        !_.includes([WorkflowApprovalStatus.ROUTED, WorkflowApprovalStatus.REROUTED, WorkflowApprovalStatus.APPROVED], workflowApprovalStatus)
      )
    )
);
export const selectIsReturnToCrewDisabled = createSelector(
  selectHasPAorGateKeeperPermission,
  selectCanReturnToCrew,
  (hasPermission: boolean, canReturnToCrew: boolean): boolean =>
    !hasPermission || !canReturnToCrew
);
export const selectIsRouteToApproveDisabled = createSelector(
  selectIsApprovalDisabled,
  selectHasPAorGateKeeperPermission,
  (isApprovalDisabled: boolean, hasPermission: boolean): boolean => isApprovalDisabled || !hasPermission
);
export const selectIsApproveDeclineDisabled = createSelector(
  selectIsApprovalDisabled,
  selectHasApproverPermission,
  (isApprovalDisabled: boolean, hasPermission: boolean): boolean => isApprovalDisabled || !hasPermission
);
export const selectIsApproverCanApproveOrDecline = createSelector(
  selectProjectPermissions,
  selectWorkflowApprovalStatus,
  (projectPermissions: ProjectPermissions, workflowApprovalStatus: WorkflowApprovalStatus): boolean => {
    return UserIsInRole('Payroll Accountant', projectPermissions)
      ? false
      : !(UserIsInRole('GateKeeper', projectPermissions)
        && !_.includes([WorkflowApprovalStatus.ROUTED, WorkflowApprovalStatus.REROUTED, WorkflowApprovalStatus.APPROVED], workflowApprovalStatus));
  }
);

export const selectIsApprovalReroute = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): boolean => {
    const {approvalReroute = false} = timecard ?? {};
    return approvalReroute;
  }
);
export const selectIsApproveDeclineShortcutEnabled = createSelector(
  selectIsApproverCanApproveOrDecline,
  selectIsApproveDeclineDisabled,
  (isApproveDeclineButtons: boolean, isApproveDeclineDisabled: boolean): boolean => isApproveDeclineButtons && !isApproveDeclineDisabled
);
export const selectSelectedWorkStatusByShiftId = (shiftId: string ) => createSelector(
  selectShifts,
  (shifts: UsaShifts): ValueError<ValueError<string>[]> =>  shifts  && shifts?.workStatuses ? _
    .get(shifts?.workStatuses[shiftId], 'workStatuses', {value : []}) : {value : []}
);
export const selectJobCodeByShiftId = (shiftId: string) => createSelector(
  selectShifts,
  (shifts: UsaShifts): ValueError<string> => shifts?.shiftRates && shifts?.shiftRates[shiftId]?.jobCode
);
export const selectScheduleListByShiftId = (shiftId: string) => createSelector(
  selectJobCodeByShiftId(shiftId),
  fromTimecardEntryUISelectors.selectJobDetails,
  (jobCode: ValueError<string>, jobDetails: JobDetails[]): Schedule[] =>  {
    if (_.isEmpty(jobCode?.value)) {
      return [];
    }
    // Combine all the schedules from the jobDetails.
    return _
      .chain(jobDetails)
      .filter({code: jobCode.value})
      .map((jobDetail: JobDetails) => jobDetail.schedules ?? [])
      .flatten()
      .uniqWith(_.isEqual)
      .value();
  }
);
export const selectScheduleList = createSelector(
  fromTimecardEntryUISelectors.selectJobDetails,
  selectStartcard,
  (jobDetails: JobDetails[], startcard): Schedule[] => {
    const jobCode = _.get(startcard, 'jobCode.value', '');
    if (_.isEmpty(jobCode)) {
      return [];
    }
    // Combine all the schedules from the jobDetails that matches the user selection.
    return _
      .chain(jobDetails)
      .filter({code: jobCode})
      .map((jobDetail: JobDetails) => jobDetail.schedules ?? [])
      .flatten()
      .uniqWith(_.isEqual)
      .value();
  }
);
export const selectScheduleCodeByShiftId = (shiftId: string) => createSelector(
  selectShifts,
  (shifts: UsaShifts): ValueError<string> => shifts?.shiftRates && shifts?.shiftRates[shiftId]?.scheduleCode
);
export const selectRerateAmountByShiftId = (shiftId: string) => createSelector(
  selectShifts,
  (shifts: UsaShifts): ValueError<number> => shifts?.shiftRates && shiftId && shifts?.shiftRates[shiftId]?.rerateAmount
);
export const selectSegmentValueByShiftIdAndHeader = (shiftId: string, headerName: string) => createSelector(
  _selectAccountCodingSegmentsByShiftId(shiftId),
  selectAccountCodeConfig,
  (accountCode: AccountCode, accountCodingConfig: AccountCodeConfig): ValueError<string> => {
    const segmentIndex = _.findIndex(accountCodingConfig?.segments, (segment: AccountCodeSegmentConfig) => segment.header === headerName);
    return accountCode && accountCode?.segments && accountCode.segments[segmentIndex];
  }
);
export const selectPunchValueByShiftIdAndField = (shiftId: string, fieldName: string) => createSelector(
  selectShifts,
  (shifts: UsaShifts) => _.get(shifts?.payPunches[shiftId], fieldName)
);
export const selectWorkDetailsValueByShiftIdAndField = (shiftId: string, fieldName: string) => createSelector(
  selectShifts,
  (shifts: UsaShifts) => _.get(shifts?.workDetails[shiftId], fieldName)
);

export const selectStuntAdjustmentByShiftId = (shiftId: string) => createSelector(
  selectShifts,
  (shifts: UsaShifts) => shifts?.workStatuses && shifts?.workStatuses[shiftId]?.stuntAdjustment
);
export const selectGlShortcutIdByShiftId = (shiftId: string) => createSelector(
  _selectAccountCodingSegmentsByShiftId(shiftId),
  (accountCode: AccountCode): string => accountCode && accountCode.glShortcutId
);

export const selectSagShift = (shiftId: string) => createSelector(
  selectShifts,
  selectActiveTimecardType,
  (state: UsaShifts, activeTimecardType: TimecardTypes): Shift => state && activeTimecardType === TimecardTypes.SAG && shiftId ?
    {
      shiftId: shiftId,
      shiftDate: state.shiftDates[shiftId],
      shiftRate: state.shiftRates[shiftId],
      workDetail: GetSagDetail(state.workDetails[shiftId]),
      accountCode: state.accountCodes[shiftId],
      payPunch: state.payPunches[shiftId],
      workStatus: state.workStatuses[shiftId]
    } : null
);
export const selectWorkStatuses = createSelector(
  selectShifts,
  (state: UsaShifts): WorkStatus[] => _.map(state ? state.workStatuses : null, (v) => v)
);
export const selectSagDetails = createSelector(
  selectShifts,
  selectActiveTimecardType,
  (state: UsaShifts, activeTimecardType: TimecardTypes): SagDetail[] =>
    activeTimecardType === TimecardTypes.SAG && _.map(state ? state.workDetails : null, (workDetail) => GetSagDetail(workDetail))
);
export const selectSagWorkConditionByShiftId = (shiftId: string) => createSelector(
  selectSagDetails,
  (details: SagDetail[]): SagDetail => _.find(details, (detail: SagDetail) => detail.shiftId === shiftId)
);
// This selector is now only  used in SAG TC but in the future, we can use this selector for SAG and CREW TC
export const selectDisableMobileRefreshActionButton = createSelector(
  selectActiveTimecardType,
  (timecardType: TimecardTypes) => timecardType === TimecardTypes.SAG
);

export const selectDisableAuditHistoryReportActionButton = createSelector(
  selectActiveTimecardType,
  (timecardType: TimecardTypes) => timecardType === TimecardTypes.SAG
);

export const selectWeeklyOvertimeSummary = createSelector(
  selectRawBreakages,
  (grossPay: GrossPay) => grossPay && grossPay.weeklyOverTimeSummary
);

export const selectShowWeeklyOvertime = createSelector(
  selectWeeklyOvertimeSummary,
  (weeklyOT: WeeklyOverTimeSummary): boolean =>
    weeklyOT && _.some(_.values(weeklyOT), (value) => !!value)
);

export const selectMobileApprovalLoops = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): MobileApprovalLoop[] => timecard && timecard.mobileApprovalLoops
);

export const selectDisplayPerDiemDayCount = createSelector(
  selectStartcard,
  (startcard: Startcard): boolean => startcard && (startcard?.workLocation?.countryCode?.value === 'CAN')
);
export const selectPerDiemDayCount = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): ValueError<number> => timecard && timecard.perDiemDayCount
);
export const selectHtgValidationErrors = createSelector(
  selectTimecard,
  (timecard: UsaTimecard): ValidationError[] => {
    const htgExceptions = _.get(timecard, 'rawBreakages.htgExceptions', []);
    if (_.isEmpty(htgExceptions)) {
      return [];
    }
    const tmp: ValidationError[] = _
      .chain(htgExceptions)
      .filter(({errorCode}) => errorCode.indexOf('HTG_VAL_ERR_') !== -1)
      .map(({errorMessage}) => ({
        area: Area.Timecard,
        field: Field.HTG,
        message: errorMessage,
        errorType: ErrorType.SUBMITTABLE,
        entityId: ''
      }))
      .value();
    console.log(`[HTG Validation Error Selector]: ${JSON.stringify(tmp)}`);
    return tmp;
  }
);
export const selectIsRefreshMobileDisabled = createSelector(
  selectIsSavedTimecard,
  selectIsTimecardSubmitted,
  selectIsMobileTimecard,
  selectWorkflowApprovalStatus,
  (isSavedTimecard: boolean, isTimecardSubmitted: boolean, isMobileTimecard: boolean, workflowApprovalStatus: WorkflowApprovalStatus): boolean =>
    !isSavedTimecard || isTimecardSubmitted || !isMobileTimecard
    || (_.includes([WorkflowApprovalStatus.ROUTED, WorkflowApprovalStatus.REROUTED, WorkflowApprovalStatus.APPROVED], workflowApprovalStatus))
);
export const selectIsVAPayrollExceptionTimecard = createSelector(
  selectTimeCardEntryRootState,
  (state: TimecardEntryState): boolean => state.ui.isVaPayrollExceptionTimecard
);
